/**
 * The admin section's last mocked read — the **cross-version diff**, and nothing else.
 *
 * survey-api has no diff endpoint, and unlike everything that used to live here, none is
 * pending: SR-123's tickets are all delivered and none of them owed one. The data-model
 * design names `question_id` as the diff and migration key (§3.4), so the need is real and
 * the shape below is a proposal for what a route should answer with.
 *
 * This file has shrunk four times as endpoints arrived — responses left for
 * `oversight-api.ts` (SR-117), accounts for `accounts-api.ts` (SR-124), and the question
 * bank, option lists, versions and ranking cycles for `definitions-api.ts` and
 * `cycles-api.ts` (SR-123). Each time the pages kept their shape and the module they await
 * changed, which is what makes "is this screen real" answerable by reading its imports.
 *
 * The two properties that mattered when this was a whole section still matter for the one
 * card left: it resolves on a timer, so the loading path is a real code path; and it is
 * cloned on the way out, so a page that mutates what it rendered cannot reach into the store.
 */

import { QUESTIONS, VERSIONS } from './fixtures';
import type { ISurveyVersion, IVersionDiff } from './contracts';

/** Long enough to see a skeleton, short enough not to feel broken. */
const LATENCY_MS = 320;

/**
 * The mutable store. Deep-copied out of the fixtures so an edit cannot write through to
 * the module the fixtures live in — which would survive a "reload" of the store and make
 * the mock's own reset meaningless.
 */
const store = {
  questions: clone(QUESTIONS),
  versions: clone(VERSIONS)
};

function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

async function answer<T>(value: T): Promise<T> {
  await new Promise((resolve) => setTimeout(resolve, LATENCY_MS));
  // Cloned on the way out too: a page that mutates what it rendered must not reach into
  // the store, since the real client hands out parsed JSON nobody else holds.
  return clone(value);
}

export const adminApi = {
  // Accounts are **not** here any more: they read survey-api's account routes through
  // `accounts-api.ts` (SR-124 for the list, SR-228's roles and kind for the writes). The
  // fixture that used to stand in for them is gone rather than left unreferenced — this
  // module's whole claim is that importing it means "this screen is not real yet", and a
  // dead half would make that claim unreadable.

  // -------------------------------------------------------------------------
  // Survey metadata
  // -------------------------------------------------------------------------







  /**
   * The versions the **diff** can be taken between — not the version list any more, which
   * reads `GET /admin/definitions/surveys` through `definitions-api.ts` (SR-123 ticket 1).
   *
   * It survives because `diffVersions` below is still a fixture, and a mock computation has
   * to be driven by mock inputs to mean anything. **Neither set of version numbers is a
   * subset of the other**, checked against the dev server: this fixture holds RIA_FIRM v1–v3
   * plus one version each of FA, FSP and Teams, while survey-api publishes RIA_FIRM v1–v2 and
   * v1–v3 for the other three. Driving the card from the real list would therefore both lose
   * the only diff the fixture can actually compute (RIA_FIRM v2→v3, whose v3 the server does
   * not have) and offer FA v2→v3, which the fixture would answer by reporting every FA
   * question as removed — output that looks like a finding and is an artifact. Data that is
   * visibly fake is the better failure.
   */
  diffableVersions(): Promise<ISurveyVersion[]> {
    return answer(
      [...store.versions].sort(
        (left, right) =>
          left.surveyCode.localeCompare(right.surveyCode) || right.versionNo - left.versionNo
      )
    );
  },

  /**
   * Diffs two versions of one survey by bank question — the design's diff key, since a
   * usage code can be re-lettered while the question stays the same.
   *
   * Derived from the questions' placements rather than stored: the mock holds no section or
   * usage tables, and a diff computed from what the screens already show cannot disagree
   * with them.
   */
  diffVersions(surveyCode: string, from: number, to: number): Promise<IVersionDiff> {
    const placementsAt = (versionNo: number) =>
      store.questions.flatMap((question) =>
        question.placements
          .filter(
            (placement) =>
              placement.surveyCode === surveyCode && placement.versionNo === versionNo
          )
          .map((placement) => ({ question, placement }))
      );

    const before = placementsAt(from);
    const after = placementsAt(to);
    const key = (entry: (typeof before)[number]) => entry.question.code;

    const entry = (item: (typeof before)[number]) => ({
      questionCode: item.question.code,
      usageCode: item.placement.usageCode,
      sectionCode: item.placement.sectionCode,
      label: item.placement.labelOverride ?? item.question.defaultLabel
    });

    const added = after.filter((item) => !before.some((old) => key(old) === key(item))).map(entry);
    const removed = before.filter((item) => !after.some((next) => key(next) === key(item))).map(entry);

    const changed = after.flatMap((item) => {
      const old = before.find((candidate) => key(candidate) === key(item));
      if (!old) return [];

      const changes = [];
      const oldLabel = old.placement.labelOverride ?? old.question.defaultLabel;
      const newLabel = item.placement.labelOverride ?? item.question.defaultLabel;
      if (oldLabel !== newLabel) {
        changes.push({ ...entry(item), field: 'label' as const, before: oldLabel, after: newLabel });
      }
      if (old.placement.sectionCode !== item.placement.sectionCode) {
        changes.push({
          ...entry(item),
          field: 'section' as const,
          before: old.placement.sectionCode,
          after: item.placement.sectionCode
        });
      }
      return changes;
    });

    return answer({ surveyCode, from, to, added, removed, changed });
  },

  // Everything else that used to be here reads survey-api now. What is left is what no
  // endpoint answers.
};
